import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  defaultGlossaryVisibility,
  FORM_CONTENT_TYPE,
  hcMetadataStatus,
  visibilitySelectionGlossaries,
} from 'src/commons/constants';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import {
  Button,
  ColumnLayout,
  Container,
  Flashbar,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import { Page } from 'src/routes';
import { createGlossary } from 'src/api/catalog';
import {
  BUSINESS_GLOSSARY_TYPE,
  glossaryTypeDisplayName,
  GOVERNANCE_TAG_TYPE,
  USER_TYPE,
} from 'src/components/metadata/constants';
import { getGlossariesForOwner } from 'src/components/workspaces/common/common';

export interface CreateBusinessGlossaryProps extends CommonPageProps {
  location: any;
  glossarySubType?: string;
}

const CreateBusinessGlossary = (props: CreateBusinessGlossaryProps) => {
  const [notifications, setNotifications] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [ownerId, setOwnerId] = useState(undefined);
  const [name, setName] = useState(undefined);
  const [description, setDescription] = useState(undefined);
  // Allow only User type to be created from Omni. All global ones are created by Omni - through HC CI/CD.
  const glossaryType = USER_TYPE;
  const [glossaryValue, setGlossaryValue] = useState(undefined);
  const [visibility, setVisibility] = useState(defaultGlossaryVisibility);
  const [parentGlossaryId, setParentGlossaryId] = useState(undefined);
  const [globalGlossaryOptions, setGlobalGlossaryOptions] = useState([]);
  const [status, setStatus] = useState(undefined);
  const glossarySubType = props.glossarySubType ?? BUSINESS_GLOSSARY_TYPE;
  const glossarySubTypeName = glossaryTypeDisplayName.get(glossarySubType);
  const isGovernanceTag = GOVERNANCE_TAG_TYPE === glossarySubType;

  useEffect(() => {
    props.setContentType(FORM_CONTENT_TYPE);
    const activeGroup = props.activeGroup;
    const activeWorkspace = props.activeWorkspace;
    const ownerId = activeWorkspace ? activeWorkspace.workspaceId : activeGroup;
    setOwnerId(ownerId);
    // ParentGlossaryId is needed for governance tag type and if type is User.
    if (isGovernanceTag) getGlossaryOptions(ownerId);
  }, []);

  const getGlossaryOptions = async (ownerId: string) => {
    setButtonLoading(true);
    try {
      const glossaries = await getGlossariesForOwner(ownerId, glossarySubType);
      setGlobalGlossaryOptions(
        glossaries.map((gl) => {
          return { label: gl.GlossaryValue, value: gl.GlossaryId };
        }),
      );
    } catch (err) {
      setNotification('error', `Unable to load all global ${glossarySubTypeName}. ${err.message}`);
    } finally {
      setButtonLoading(false);
    }
  };

  const handleCreateBusinessGlossary = async () => {
    try {
      const isUserGovernanceTag = isGovernanceTag && USER_TYPE === glossaryType;
      let createGlossaryResult = await createGlossary({
        OwnerId: ownerId,
        Name: isGovernanceTag ? undefined : name,
        Description: description,
        Status: status.value,
        GlossaryType: glossaryType,
        GlossaryValue: glossaryValue,
        Visibility: visibility.value,
        GlossarySubType: glossarySubType,
        ParentGlossaryId: isUserGovernanceTag ? parentGlossaryId?.value : undefined,
      });
      if (createGlossaryResult.Message == 'Success') {
        props.setRedirect(isGovernanceTag ? Page.GOVERNANCE_TAGS : Page.BUSINESS_GLOSSARIES, false, {
          status: 'success',
          message: `${glossarySubTypeName} created successfully`,
        });
      } else {
        setNotification('error', `Failed to create ${glossarySubTypeName}. Reason: ${createGlossaryResult.Message}`);
      }
    } catch (err) {
      setNotification('error', `Error in creating ${glossarySubTypeName}. ${err.message}`);
    } finally {
      setButtonLoading(false);
    }
  };

  const setNotification = async (header, message) => {
    if (header === 'error') {
      setNotifications([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  return (
    <>
      <Flashbar items={notifications} />
      <Form
        header={
          <Header variant='h1' description={`Please specify the details of your ${glossarySubTypeName}.`}>
            Create {glossarySubTypeName}
          </Header>
        }
        actions={
          <SpaceBetween direction='horizontal' size='s'>
            <Button
              variant='link'
              onClick={() => props.setRedirect(isGovernanceTag ? Page.GOVERNANCE_TAGS : Page.BUSINESS_GLOSSARIES)}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              loading={buttonLoading}
              onClick={handleCreateBusinessGlossary}
              disabled={!description || !glossaryType || !status || !glossaryValue}
            >
              {'Create'}
            </Button>
          </SpaceBetween>
        }
      >
        <Container header={<Header variant='h2'>{glossarySubTypeName} details</Header>}>
          <ColumnLayout>
            <SpaceBetween size='m'>
              <FormField label={<div>Owner</div>} description={`Owner of the ${glossarySubTypeName}.`}>
                <Input
                  name='ownerId'
                  placeholder='Glossary owner'
                  value={ownerId}
                  ariaRequired={true}
                  disabled={true}
                />
              </FormField>
              {!isGovernanceTag && (
                <FormField label={<div>Name</div>} description={`Name of the ${glossarySubTypeName}.`}>
                  <Input
                    name='name'
                    placeholder='Name of the glossary'
                    value={name}
                    onChange={({ detail }) => setName(detail.value)}
                    ariaRequired={true}
                  />
                </FormField>
              )}
              <FormField
                label={<div>{isGovernanceTag ? 'Governance tag name' : 'Value'}</div>}
                description={`${isGovernanceTag ? 'Name of governance tag' : 'Value of business glossary'}.`}
              >
                <Input
                  name='value'
                  placeholder={isGovernanceTag ? 'Name of governance tag' : 'Value of the glossary'}
                  value={glossaryValue}
                  onChange={({ detail }) => setGlossaryValue(detail.value)}
                  ariaRequired={true}
                />
              </FormField>
              <FormField
                label={<div>Description</div>}
                description={`Description of the ${glossarySubTypeName.toLowerCase()}.`}
              >
                <Input
                  name='description'
                  placeholder={`Description of the ${glossarySubTypeName.toLowerCase()}.`}
                  value={description}
                  onChange={({ detail }) => setDescription(detail.value)}
                  ariaRequired={true}
                />
              </FormField>
              {isGovernanceTag && USER_TYPE === glossaryType && (
                <FormField
                  label={<div>Parent tag</div>}
                  description={`Select a parent tag. A Global parent tag is required for User tag.`}
                >
                  <Select
                    name='parentGlossaryId'
                    selectedOption={parentGlossaryId}
                    ariaRequired={true}
                    placeholder='Choose a parent governance tag'
                    onChange={({ detail }) => setParentGlossaryId(detail.selectedOption)}
                    options={globalGlossaryOptions}
                    selectedAriaLabel='Selected'
                  />
                </FormField>
              )}
              <FormField label={<div>Status</div>} description='Only Enabled entities can be associated to resources.'>
                <Select
                  name='status'
                  selectedOption={status}
                  ariaRequired={true}
                  placeholder='Choose a type'
                  onChange={({ detail }) => setStatus(detail.selectedOption)}
                  options={hcMetadataStatus}
                  selectedAriaLabel='Selected'
                />
              </FormField>
              <FormField label='Visibility' description={`Visibility of ${glossarySubTypeName}.`}>
                <Select
                  name='visibility'
                  placeholder='Public'
                  selectedOption={visibility}
                  options={visibilitySelectionGlossaries}
                  ariaRequired={true}
                  onChange={({ detail }) => {
                    setVisibility(detail.selectedOption);
                  }}
                  disabled={false}
                />
              </FormField>
            </SpaceBetween>
          </ColumnLayout>
        </Container>
      </Form>
    </>
  );
};

export default CreateBusinessGlossary;
